import {Time, TimeInterval} from 'yougile-util'
import {ChatMessageJSON} from './chat-message'
import {DataObjectId, isDataObjectId} from './data-object'
import {StickerSettingsInBoard} from './hubs/board'
import {StickerColor} from './sticker-constants'

export type StickerValue<T> = T | 'not-set' | 'not-pinned'
export const isSetSticker = <T>(
  state: StickerValue<T | 'not-set' | undefined> | 'not-set' | undefined
): state is T => {
  return !!state && state !== 'not-pinned' && state !== 'not-set'
}

export const assignedStickerStateToIds = (state: any): DataObjectId[] => {
  return !state
    ? []
    : state instanceof Array
      ? state
      : isDataObjectId(state)
        ? [state]
        : []
}

export const getMessageAssignIds = (
  message: ChatMessageJSON
): DataObjectId[] => {
  const assignedStr = message?.properties?.assigned
    ? (message.properties.assigned as string)
    : ''
  const assigned = (
    !assignedStr
      ? []
      : assignedStr.indexOf(',')
        ? assignedStr.split(',')
        : [assignedStr]
  ) as DataObjectId[]
  return assigned
}

export const extractStickerState = <T>(
  state: StickerValue<T> | undefined
): T | undefined => {
  return isSetSticker(state) ? state : undefined
}
export const mapStickerState = <T, U>(
  state: StickerValue<T>,
  mapper: (t: T) => U
): StickerValue<U> => {
  return isSetSticker(state) ? mapper(state) : state
}

export const extractStickerStateWith = <T, U>(
  state: StickerValue<T>,
  mapper: (x: T) => U
): U | undefined => {
  return extractStickerState(mapStickerState(state, mapper))
}

export const combineDeadlines = (
  deadlines: (DeadlineStickerEntry | undefined)[]
): {deadline: number; startDate: number} | undefined => {
  let startDate = 0
  let deadline = 0

  for (const dl of deadlines) {
    if (!dl?.deadline) {
      continue
    }
    const start = dl.startDate ?? dl.deadline
    startDate = startDate ? Math.min(start, startDate) : start
    deadline = Math.max(dl.deadline, deadline)
  }

  return deadline ? {deadline, startDate} : undefined
}

export interface TimerStickerEntry {
  time: string // e.g.: 0w1d5h0m0s
  by: DataObjectId
  running: boolean
  timestamp: number
}

export interface RepeatStickerEntry {
  templateId: DataObjectId // template id
  repeatedNumber: number
}

export type RangePoint = 'start' | 'end'

export interface DeadlineStickerEntry {
  deadline?: number
  startDate?: number
  order?: number
  timestamp: number
  by: DataObjectId
  withTime?: boolean
  notifyBefore?: number
}

export interface DeadlineStickerValue {
  current: DeadlineStickerEntry
  history: DeadlineStickerEntry[]
}

export interface SprintStickerState {
  id: StickerStateId
  name: string
  begin?: number
  end?: number
}

export interface TimerStickerValue {
  current: TimerStickerEntry
  history: TimerStickerEntry[]
}

export interface StringStickerState {
  id: StickerStateId
  name: string
  color: StickerColor
}

export interface TextStickerState {
  id: StickerStateId
  text: string
}

export interface NumericStickerState {
  id: StickerStateId
  text: string
}

export interface TimetrackerValue {
  plan: number // number in hours
  work: number // number in hours
}

export interface TimetrackerBoardSettings {
  complete: {[id: string]: number} // task completion columns
  exclude: {[id: string]: number} // excluded columns
}

export type GenericStickerState =
  | DeadlineStickerValue
  | TimerStickerValue
  | SprintStickerState
  | DataObjectId
  | (DataObjectId | 'author')[]
  | StringStickerState
  | TimetrackerValue
  | RepeatStickerEntry
  | string
  | number
  | null

export type StickerStateId = string & {__type: 'StickerStateId'}

export const timerToInterval = (
  value: TimerStickerEntry | undefined,
  forward: boolean
): TimeInterval | undefined => {
  if (!value || !value.time || !value.timestamp) {
    return undefined
  }
  const {time, timestamp, running} = value
  const initial = TimeInterval.fromString(time)
  const passed = forward
    ? TimeInterval.fromDates(Time.now(), timestamp)
    : TimeInterval.fromDates(timestamp, Time.now())
  return !running ? initial : initial.add(passed)
}

export const isAuthor = (settings: StickerSettingsInBoard): boolean => {
  if (!settings.defaultState) {
    return true
  }

  const state = settings.defaultState
  if (!Array.isArray(state)) {
    return false
  }

  return state.length === 1 && state[0] === 'author'
}

/**
 * Shift deadline start to provided time while keeping deadline length
 */
export const shiftDeadlineKeepingLength = (
  deadline: DeadlineStickerValue,
  toTime: number,
  userId: DataObjectId
): DeadlineStickerValue => {
  if (!deadline?.current?.deadline) {
    return deadline
  }

  const end = deadline.current.deadline
  const start = deadline.current.startDate
  const newDeadline = {...deadline}
  newDeadline.history = [...newDeadline.history, newDeadline.current]

  if (start) {
    const newStartDate = new Date(toTime)
    // If a task start date is shifted to Sat or Sun, shift it to Mon
    if (newStartDate.getDay() === 6) {
      newStartDate.setDate(newStartDate.getDate() + 2)
    } else if (newStartDate.getDay() === 0) {
      newStartDate.setDate(newStartDate.getDate() + 1)
    }

    // Keep length in working days after shifting
    let workingDays = Time.workingDaysBetween(start, end)
    if (Time.canTimeBeOnSameDate(start, end) && workingDays > 0) {
      workingDays -= 1
    }
    const newEndDate = Time.addWorkingDays(newStartDate, workingDays)

    Time.applyTimeOffset(newStartDate, new Date(start))
    Time.applyTimeOffset(newEndDate, new Date(end))

    newDeadline.current = {
      ...newDeadline.current,
      startDate: newStartDate.getTime(),
      deadline: newEndDate.getTime(),
      timestamp: Date.now(),
      by: userId
    }
  } else {
    const newEndDate = new Date(toTime)

    // If a one-day task is shifted to Sat or Sun, shift it to Mon
    if (newEndDate.getDay() === 6) {
      newEndDate.setDate(newEndDate.getDate() + 2)
    } else if (newEndDate.getDay() === 0) {
      newEndDate.setDate(newEndDate.getDate() + 1)
    }

    Time.applyTimeOffset(newEndDate, new Date(end))

    newDeadline.current = {
      ...newDeadline.current,
      deadline: newEndDate.getTime(),
      timestamp: Date.now(),
      by: userId
    }
  }

  // Update only if something was changed
  return newDeadline.current.deadline === deadline.current.deadline &&
    newDeadline.current.startDate === deadline.current.startDate
    ? deadline
    : newDeadline
}
